import type { PanelEntry } from "./api";
import { readDraft } from "./drafts";

/** Locally created work pages remain destinations even when declared as support. */
export function createdWorkPage(panel: PanelEntry): boolean {
  return panel.origin === "local" && panel.audience === "owner" && panel.slot === "main"
    && !panel.bootstrap && !panel.interaction && !panel.settings && !panel.configures;
}

/** A registered local entry replaces its template in navigation, not in authority. */
export function workspaceEntries<T extends PanelEntry>(panels: T[]): T[] {
  panels = panels.filter(panel => !panel.bootstrap && !panel.interaction
    && (!panel.settings && !panel.configures || Boolean(panel.workspace)));
  const chosen = new Map<string, T>();
  for (const panel of panels) {
    const group = panel.workspace;
    if (!group?.key || group.role === "support") continue;
    if (group.role !== "entry" && group.role !== "template") continue;
    const previous = chosen.get(group.key);
    if (!previous || (group.role === "entry" && previous.workspace?.role !== "entry")) chosen.set(group.key, panel);
  }
  return panels.filter(panel => panel.workspace?.role === "support" ? createdWorkPage(panel)
    : !panel.workspace?.key || chosen.get(panel.workspace.key) === panel);
}

/**
 * The pages whose own read puts items on 总览: each workspace's entry, and a support page that declares such a read.
 * Bundled support pages are reached from their workspace, but what goes on in them (a research that runs) is the
 * Agent's work all the same. Locally created pages are already independent entries. A template an entry replaced
 * is not listed: the entry carries what it declared.
 */
export function overviewPanels<T extends PanelEntry>(panels: T[]): T[] {
  return [...workspaceEntries(panels), ...panels.filter(panel => panel.workspace?.role === "support" && !createdWorkPage(panel)
    && Boolean(panel.overview_preview) && !panel.bootstrap && !panel.interaction)];
}

/** Remember an independent page by its own key, or a bundled workspace by the shared key. */
export function workspaceMemory(panel: PanelEntry): string {
  // Support pages share the bundled workspace's object only once provenance is known.
  return panel.workspace?.key && (panel.workspace.role !== "support" || panel.origin === "shipped")
    ? `workspace:${panel.workspace.key}` : `panel:${panel.key}`;
}

/**
 * The workspace an Agent's home opens straight into: its one overview workspace, however many other pages
 * it has; with several, the one this browser opened last (`lastOpened`, from `lib/lastWorkspace`). With
 * neither known the home is a page of its own, where the owner chooses. Only declarations decide
 * (`overview`), so the shell names no plugin. The Agent home and the rail both ask this, so the rail
 * knows where its own press ends up.
 */
export function agentLanding(panels: PanelEntry[], lastOpened: string | null = null): PanelEntry | null {
  const owner = panels.filter(panel => panel.slot === "main" && panel.audience === "owner");
  const entries = workspaceEntries(owner).filter(panel => panel.overview === true);
  if (entries.length === 1) return entries[0];
  return lastOpened ? entries.find(panel => workspaceMemory(panel) === lastOpened) ?? null : null;
}

export function workspaceTitle(panel: PanelEntry): string {
  if (panel.workspace?.role === "support" && panel.origin !== "shipped") return panel.title;
  return panel.workspace?.title?.trim() || panel.title;
}

/** Only live plugin contributions may initialize the workspace conversation. */
export function conversationOpening(panels: PanelEntry[]): string | undefined {
  for (const panel of panels) {
    if (panel.audience !== "owner") continue;
    const value = panel.workspace?.conversation;
    if (!value || typeof value !== "object") continue;
    const { opening } = value as Record<string, unknown>;
    if (typeof opening === "string" && opening.trim()) return opening.trim();
  }
}

export function sameWorkspace(left: PanelEntry | undefined, right: PanelEntry): boolean {
  return Boolean(left && (left.key === right.key || workspaceMemory(left) === workspaceMemory(right)));
}

/** Return to this destination's saved object and conversation within its own Agent scope. */
export function workspaceHref(panel: PanelEntry, sessionId?: string, scope?: string): string {
  const context = readDraft<Record<string, string>>(`navigation:${workspaceMemory(panel)}`, {}, scope);
  return panelHref(panel.key, {
    ...context,
    ...(!context.session && sessionId ? { session: sessionId } : {}),
  });
}

export function panelHref(key: string, context: Record<string, string> = {}): string {
  const params = new URLSearchParams(context);
  return `#/panel/${encodeURIComponent(key)}${params.size ? `?${params}` : ""}`;
}
